import type { GameStateManager } from '../game/GameState';
import type { GameData, ResearchCompletionResult, GameSpeed } from '../game/types';
import { AudioManager } from '../audio/AudioManager';

export class ResearchCompletionModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onClose: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if (this.container.classList.contains('hidden')) return;
      
      const target = e.target as HTMLElement;
      if (target.classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Enter' || event.code === 'Escape' || event.code === 'Space') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(result: ResearchCompletionResult): void {
    if (!this.container.classList.contains('hidden')) {
      return; // Already showing
    }

    this.container.classList.remove('hidden');
    this.render(result);
    this.gameState.setPausedByModal(true);
    AudioManager.getInstance().playMajorEvent();
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    if (this.container.classList.contains('hidden')) {
      return; // Already hidden
    }

    // CRITICAL: Dequeue FIRST, before anything else
    this.gameState.dequeueResearchCompletion();

    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    // Defer onClose to next tick to prevent synchronous re-showing of same modal
    setTimeout(() => {
      this.onClose();
    }, 0);
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private render(result: ResearchCompletionResult): void {
    const strings = this.gameData.uiStrings;
    const researcherName = result.researcherName;

    const researchTypeNames = result.usedTypes.map(type => {
      const researchType = this.gameData.researchTypes.find(rt => rt.id === type);
      return researchType?.id || type;
    }).join(', ');

    const propertyColors = strings.propertyColors || {};
    const propertyDisplay = result.allKnownProperties.map((prop, index) => {
      const symbol = strings.propertySymbols?.[prop.toLowerCase()] || prop;
      const name = strings.properties?.[prop.toLowerCase()] || prop;
      const isNew = prop === result.newlyRevealedProperty;
      const propColor = propertyColors[prop.toLowerCase()] || '#6bb0ff';
      return `
        <div class="property-item ${isNew ? 'newly-revealed' : ''}" style="border-color: ${propColor}; color: ${propColor};">
          <span class="property-symbol" style="color: ${propColor};">${symbol}</span>
          <span class="property-name" style="color: ${propColor};">${name}</span>
          ${isNew ? '<span class="new-badge">NEW</span>' : ''}
        </div>
      `;
    }).join('');

    const effectsList: string[] = [];

    if (result.effects.lethalKilled) {
      const lethalColor = propertyColors['lethal'] || '#ff4444';
      const lethalSymbol = strings.propertySymbols?.['lethal'] || '☠';
      effectsList.push(`<div class="effect-item" style="border-left-color: ${lethalColor}; color: ${lethalColor}">${lethalSymbol} Researcher ${researcherName} was killed by Lethal property</div>`);
    } else if (result.effects.lethalBlocked) {
      effectsList.push(`<div class="effect-item" style="border-left-color: #44ff44; color: #66ff66">✓ Lethal attack blocked by Silver Bullet</div>`);
    }

    if (result.effects.memeticSanityLoss > 0) {
      const memeticColor = propertyColors['memetic'] || '#ffaa44';
      const memeticSymbol = strings.propertySymbols?.['memetic'] || 'Ψ';
      effectsList.push(`<div class="effect-item" style="border-left-color: ${memeticColor}; color: ${memeticColor}">${memeticSymbol} Lost ${result.effects.memeticSanityLoss} Sanity from Memetic property</div>`);
    }

    if (result.effects.antiepistemicRewardSkipped) {
      const antiepistemicColor = propertyColors['antiepistemic'] || '#aa44ff';
      const antiepistemicSymbol = strings.propertySymbols?.['antiepistemic'] || '⛓';
      effectsList.push(`<div class="effect-item" style="border-left-color: ${antiepistemicColor}; color: ${antiepistemicColor}">${antiepistemicSymbol} ${result.effects.antiepistemicReason}</div>`);
    }

    if (result.effects.hyperrealAnchoringReduction > 0) {
      const hyperrealColor = propertyColors['hyperreal'] || '#ff44ff';
      const hyperrealSymbol = strings.propertySymbols?.['hyperreal'] || '✦';
      effectsList.push(`<div class="effect-item" style="border-left-color: ${hyperrealColor}; color: ${hyperrealColor}">${hyperrealSymbol} Anchoring reduced by ${result.effects.hyperrealAnchoringReduction}</div>`);
    }

    if (result.effects.dormantRewardReduction > 0) {
      const dormantColor = propertyColors['dormant'] || '#888888';
      const dormantSymbol = strings.propertySymbols?.['dormant'] || '⊘';
      const reductionPercent = Math.round((result.effects.dormantRewardReduction / 3) * 100);
      effectsList.push(`<div class="effect-item" style="border-left-color: ${dormantColor}; color: ${dormantColor}">${dormantSymbol} Reward reduced by ${reductionPercent}% (${result.effects.dormantRewardReduction} Dormant)</div>`);
    }

    if (result.effects.breachingEscaped) {
      const breachingColor = propertyColors['breaching'] || '#ffaa44';
      const breachingSymbol = strings.propertySymbols?.['breaching'] || '⚡';
      const cellName = strings.locations?.[result.effects.breachingCellId || ''] || result.effects.breachingCellId;
      effectsList.push(`<div class="effect-item" style="border-left-color: ${breachingColor}; color: ${breachingColor}">${breachingSymbol} Anomaly breached and escaped to ${cellName}</div>`);
    }

    let rewardsDisplay = '';
    if (result.effects.antiepistemicRewardSkipped) {
      rewardsDisplay = '<div class="reward-item skipped">No reward - Antiepistemic requirement not met</div>';
    } else if (result.rewards.actualLargeReward > 0) {
      rewardsDisplay = `<div class="reward-item large">Major Reward: ${result.rewards.actualLargeReward} Budget</div>`;
    } else if (result.rewards.actualSmallReward > 0) {
      rewardsDisplay = `<div class="reward-item small">Minor Reward: ${result.rewards.actualSmallReward} Budget</div>`;
    } else {
      rewardsDisplay = '<div class="reward-item skipped">No reward</div>';
    }

    const isPropertyClassificationChange = result.previousPropertyCount === 1 && result.allKnownProperties.length === 2;

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content research-completion-modal" tabindex="0">
        <div class="modal-header">
          <h2>${strings.messages?.research_complete || 'Research Complete'}</h2>
          <button class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <div class="research-completion-info">
            ${isPropertyClassificationChange ? `
            <div class="completion-section property-classification-change">
              <h3>🔬 Classification Updated</h3>
              <div class="classification-change-content">
                <div class="classification-upgrade">${result.anomalyDisplayName}</div>
                <div class="classification-description">The anomaly's classification has been upgraded. We now know two of its properties, providing a more complete understanding of its nature.</div>
              </div>
            </div>
            ` : ''}
            
            <div class="completion-section">
              <h3>Researcher</h3>
              <div>${researcherName}</div>
            </div>
            
            <div class="completion-section">
              <h3>Research Methods Used</h3>
              <div>${researchTypeNames}</div>
            </div>
            
            <div class="completion-section">
              <h3>Known Properties</h3>
              <div class="properties-list">
                ${propertyDisplay}
              </div>
            </div>
            
            ${effectsList.length > 0 ? `
            <div class="completion-section">
              <h3>Effects</h3>
              <div class="effects-list">
                ${effectsList.join('')}
              </div>
            </div>
            ` : ''}
            
            <div class="completion-section">
              <h3>Rewards</h3>
              <div class="rewards-list">
                ${rewardsDisplay}
              </div>
            </div>
            
            ${result.warpUnlocked ? `
            <div class="completion-section warp-unlocked">
              <h3>Warp Unlocked</h3>
              <div>We can use this anomaly to discover others: Warp unlocked.</div>
            </div>
            ` : ''}
            
            ${result.isFullyResearched && !result.wasFullyResearchedBefore ? `
            <div class="completion-section anomaly-identified">
              <h3>🎯 Anomaly Identified</h3>
              <div>All properties are now known. The anomaly has been fully identified and can be safely dealt with.</div>
            </div>
            ` : ''}
            
            ${result.isFullyResearched ? `
            <div class="completion-section fully-researched">
              <h3>✓ Fully Researched</h3>
              <div>All properties discovered. ${result.wasFullyResearchedBefore ? 'Anomaly can be safely dismissed to containment bunker.' : 'Research again to receive major reward and dismiss to containment bunker.'}</div>
            </div>
            ` : ''}
            
            <div class="completion-section anomaly-flavor">
              <div class="anomaly-flavor-name">${result.anomalyDisplayName}</div>
              <div class="anomaly-flavor-description">${result.anomalyDescription}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-confirm button-close">Close</button>
        </div>
      </div>
    `;

    // Add button listeners - use once:true to auto-remove after one click
    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide(), { once: true });
    }

    const footerButton = this.container.querySelector('.button-close') as HTMLElement;
    if (footerButton) {
      footerButton.addEventListener('click', () => this.hide(), { once: true });
    }
  }
}
